<template>
  <div class="ai-track" v-loading="loading">
    <div class="section">
      <div class="section-title">今日概况</div>
      <el-row :gutter="16">
        <el-col :span="8" v-for="item in overviewCards" :key="item.label">
          <div class="stat-card">
            <div class="label">{{ item.label }}</div>
            <div class="value">{{ item.value }}</div>
            <div class="sub">昨日 {{ item.yesterday }}</div>
          </div>
        </el-col>
      </el-row>
    </div>

    <div class="section">
      <div class="section-head">
        <div class="section-title">访问趋势</div>
        <div>
          <el-select v-model="timeType" size="small" class="mr8" @change="loadTrend">
            <el-option v-for="t in timeOptions" :key="t.id" :label="t.name" :value="t.id" />
          </el-select>
          <el-select v-model="businessType" size="small" @change="loadTrend">
            <el-option v-for="b in businessOptions" :key="b.id" :label="b.name" :value="b.id" />
          </el-select>
        </div>
      </div>
      <el-table :data="trendList" border size="small" max-height="280">
        <el-table-column prop="time" label="时间" align="center" />
        <el-table-column prop="count" label="访问量" align="center" />
      </el-table>
      <div class="tip">合计：{{ trendTotal }}</div>
    </div>

    <div class="section">
      <div class="section-head">
        <div class="section-title">场景统计</div>
        <el-select v-model="sceneTimeType" size="small" @change="loadScene">
          <el-option v-for="t in timeOptions" :key="t.id" :label="t.name" :value="t.id" />
        </el-select>
      </div>
      <el-table :data="sceneRows" border size="small">
        <el-table-column prop="name" label="指标" align="center" />
        <el-table-column prop="cardCount" label="名片" align="center" />
        <el-table-column prop="appointmentCount" label="预约" align="center" />
        <el-table-column prop="shopCount" label="商城" align="center" />
        <el-table-column prop="dynamicCount" label="动态" align="center" />
        <el-table-column prop="webCount" label="官网" align="center" />
        <el-table-column prop="videoCount" label="媒体" align="center" />
      </el-table>
    </div>

    <div class="section">
      <div class="section-head">
        <div class="section-title">访问排行</div>
        <el-select v-model="seeType" clearable size="small" placeholder="全部类型" @change="loadRank">
          <el-option v-for="s in seeTypeOptions" :key="String(s.id)" :label="s.name" :value="s.id" />
        </el-select>
      </div>
      <el-table :data="rankList" border size="small">
        <el-table-column type="index" label="排名" width="70" align="center" />
        <el-table-column prop="nickName" label="访客" align="center" show-overflow-tooltip />
        <el-table-column prop="seeTypeName" label="最近访问" align="center" />
        <el-table-column prop="num" label="访问次数" align="center" />
        <el-table-column label="操作" width="120" align="center">
          <template slot-scope="scope">
            <el-button type="text" @click="goCustomer(scope.row)">查看画像</el-button>
          </template>
        </el-table-column>
      </el-table>
    </div>
  </div>
</template>

<script>
import {
  situationStatistics,
  timeStatistics,
  sceneStatistics,
  getVisitRankingList
} from '@/api/radar'

const SEE_TYPE_MAP = {
  1: '名片',
  2: '产品',
  3: '官网',
  4: '文章',
  5: '预约',
  6: '视频营销'
}

export default {
  name: 'AiTrack',
  data() {
    return {
      loading: false,
      overview: {},
      timeType: 0,
      businessType: 1,
      sceneTimeType: 0,
      seeType: '',
      trendList: [],
      trendTotal: 0,
      sceneData: {
        visitUserCount: {},
        visitNewUserCount: {},
        visitCount: {}
      },
      rankList: [],
      timeOptions: [
        { id: 0, name: '今日' },
        { id: 1, name: '昨日' },
        { id: 2, name: '近7天' },
        { id: 3, name: '近30天' }
      ],
      businessOptions: [
        { id: 1, name: '名片' },
        { id: 2, name: '产品' },
        { id: 3, name: '官网' },
        { id: 4, name: '文章' },
        { id: 5, name: '预约' }
      ],
      seeTypeOptions: [
        { id: '', name: '全部' },
        { id: 1, name: '名片' },
        { id: 2, name: '产品' },
        { id: 3, name: '官网' },
        { id: 4, name: '文章' },
        { id: 5, name: '预约' },
        { id: 6, name: '视频营销' }
      ]
    }
  },
  computed: {
    overviewCards() {
      const o = this.overview || {}
      return [
        { label: '访问人数', value: o.visitUserCount || 0, yesterday: o.yesterdayVisitUserCount || 0 },
        { label: '新用户', value: o.visitNewUserCount || 0, yesterday: o.yesterdayVisitNewUserCount || 0 },
        { label: '访问次数', value: o.visitCount || 0, yesterday: o.yesterdayVisitCount || 0 }
      ]
    },
    sceneRows() {
      const d = this.sceneData || {}
      const pick = (obj = {}) => ({
        cardCount: obj.cardCount || 0,
        appointmentCount: obj.appointmentCount || 0,
        shopCount: obj.shopCount || 0,
        dynamicCount: obj.dynamicCount || 0,
        webCount: obj.webCount || 0,
        videoCount: obj.videoCount || 0
      })
      return [
        { name: '访问人数', ...pick(d.visitUserCount) },
        { name: '新用户数', ...pick(d.visitNewUserCount) },
        { name: '访问次数', ...pick(d.visitCount) }
      ]
    }
  },
  created() {
    this.refreshAll()
  },
  methods: {
    async refreshAll() {
      this.loading = true
      try {
        await Promise.all([this.loadOverview(), this.loadTrend(), this.loadScene(), this.loadRank()])
      } finally {
        this.loading = false
      }
    },
    async loadOverview() {
      const res = await situationStatistics({})
      this.overview = (res && res.data) || {}
    },
    async loadTrend() {
      const res = await timeStatistics({
        timeType: this.timeType,
        businessType: this.businessType
      })
      const data = (res && res.data) || {}
      this.trendList = data.cell || []
      this.trendTotal = data.total || 0
    },
    async loadScene() {
      const res = await sceneStatistics({ timeType: this.sceneTimeType })
      this.sceneData = (res && res.data) || {
        visitUserCount: {},
        visitNewUserCount: {},
        visitCount: {}
      }
    },
    async loadRank() {
      const params = { timeType: this.timeType }
      if (this.seeType !== '' && this.seeType !== null && this.seeType !== undefined) {
        params.seeType = this.seeType
      }
      const res = await getVisitRankingList(params)
      const list = (res && res.data) || []
      this.rankList = list.map(item => ({
        ...item,
        seeTypeName: SEE_TYPE_MAP[item.seeType] || '-'
      }))
    },
    goCustomer(row) {
      if (!row) return
      if (row.cardId) {
        this.$router.push({
          path: '/customerDetall',
          query: { id: row.cardId, tab: 'profile', userId: row.userId || '' }
        })
      } else if (row.userId) {
        this.$message.info('该访客暂无客户名片，可在客户管理中跟进')
      }
    }
  }
}
</script>

<style scoped lang="scss">
.section {
  margin-bottom: 28px;
}
.section-title {
  font-size: 16px;
  font-weight: 600;
  color: #17191a;
  margin-bottom: 12px;
}
.section-head {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 12px;
}
.stat-card {
  background: #f7fafa;
  border-radius: 8px;
  padding: 16px 20px;
  margin-bottom: 8px;
  .label { color: #8a8f93; font-size: 13px; }
  .value { color: #51cbcd; font-size: 28px; font-weight: 700; margin: 8px 0 4px; }
  .sub { color: #a2a6a8; font-size: 12px; }
}
.mr8 { margin-right: 8px; }
.tip { margin-top: 8px; color: #8a8f93; font-size: 13px; }
</style>
